import { addDays, format } from 'date-fns';
import { useGetDocuments } from 'entities/document/api';
import { useGetCurrentBoardId } from 'entities/user/api/hooks/useGetCurrentBoardId';
import { useEffect, useRef } from 'react';
import { useSearchParams as useSearchParamsReactRouter } from 'react-router-dom';
import { useTable2UrlState } from '../shared/ui/Table2';

export type UseDocumentsProps = {
  archived?: boolean;
};

const DEFAULT_SORTING = [{ id: 'updatedAt', desc: true }];

/**
 * Maps TanStack column ids to the `sortBy` enum values the backend
 * expects. Backend accepts DB column names in snake_case (see
 * `entities/document/api/types/get-documents-types.ts: sortBy`).
 *
 * MUST stay in sync with `meta.apiKey` on the corresponding column in
 * `pages/Documents/components/DocumentsTable/columns/*`. Single source of
 * truth is the column definition; this map mirrors it because the data
 * hook is decoupled from the column array.
 */
const API_SORT_KEY_MAP: Record<string, string> = {
  updatedAt: 'updated_at',
  contractorName: 'contractor_name',
};

/**
 * Hook to manage a document collection
 *
 */
export const useDocuments = (props: UseDocumentsProps = {}) => {
  /**
   * Hook to get the current board ID.
   */
  const boardId = useGetCurrentBoardId()!;

  /**
   * Read the `documents` param from URL (gzip/base64 encoded document IDs).
   * SMS notification links include this param to filter to specific documents.
   * Read once and clean up the URL.
   */
  const [searchParams, setSearchParams] = useSearchParamsReactRouter();
  const documentsParam = useRef(searchParams.get('documents'));

  useEffect(() => {
    if (documentsParam.current) {
      setSearchParams(
        prev => {
          prev.delete('documents');
          return prev;
        },
        { replace: true },
      );
    }
  }, [setSearchParams]);

  /**
   * Legacy support: rewrite `?packageId=xxx` → `?f.packageId=xxx` so that
   * old external links (e.g. previously shared URLs) still hit the package
   * filter after we moved table filters to the `f.*` URL convention.
   */
  const legacyPackageIdRef = useRef(searchParams.get('packageId'));

  useEffect(() => {
    if (!legacyPackageIdRef.current) {
      return;
    }

    setSearchParams(
      prev => {
        prev.delete('packageId');
        if (!prev.has('f.packageId')) {
          prev.set('f.packageId', legacyPackageIdRef.current!);
        }
        return prev;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const {
    pagination,
    setPagination,
    sorting,
    setSorting,
    columnFilters,
    debouncedColumnFilters,
    setColumnFilters,
  } = useTable2UrlState({
    defaultSorting: DEFAULT_SORTING,
    defaultPageSize: 10,
  });

  const prevBoardIdRef = useRef(boardId);
  useEffect(() => {
    if (prevBoardIdRef.current !== boardId) {
      prevBoardIdRef.current = boardId;
      setPagination(s => ({
        ...s,
        pageIndex: 0, // Reset to the first page when boardId changes
      }));
    }
  }, [boardId, setPagination]);

  /**
   * Maps the updatedAt filter values to the format expected by the API.
   * @param values - The values to map, expected to be an array of date strings.
   */
  const mappingUpdatedAtFilter = (values: unknown) => {
    const addDay = (date: string): string => {
      const parsedDate = new Date(date);
      const newDate = addDays(parsedDate, 1);
      return format(newDate, 'yyyy-MM-dd');
    };

    // Single date from URL deserialization arrives as a plain string
    // (no comma separator) — treat it as a specific day filter.
    if (typeof values === 'string' && values !== '') {
      return { from: values, to: addDay(values) };
    }

    if (!Array.isArray(values)) {
      return {};
    }

    if (!values[0] && !values[1]) {
      return {};
    }

    if (values.length === 1) {
      return {
        from: values[0],
        to: addDay(values[0]),
      };
    }

    if (values.length === 2) {
      return {
        from: values[0],
        to: values[1] && addDay(values[1]),
      };
    }

    return {};
  };

  /**
   * Maps the column filters to the format expected by the API.
   * @param filters - Array of filters to map
   */
  const mappingFilters = (filters: { id: string; value: unknown }[]) =>
    filters.reduce<Record<string, unknown>>((acc, filter) => {
      if (filter.value !== undefined && filter.value !== null) {
        if (filter.id === 'updatedAt') {
          return { ...acc, ...mappingUpdatedAtFilter(filter.value) };
        }

        acc[filter.id] = filter.value;
      }
      return acc;
    }, {});

  const mappingSorting = (): {
    sortBy: string;
    sortDir: 'asc' | 'desc';
  } => {
    if (sorting.length === 0) {
      return { sortBy: 'updated_at', sortDir: 'desc' };
    }

    const { id, desc } = sorting[0];
    return {
      sortBy: API_SORT_KEY_MAP[id] ?? id,
      sortDir: desc ? 'desc' : 'asc',
    };
  };

  /**
   * Fetches documents using the useGetDocuments hook
   */
  const { data, refetch, isLoading } = useGetDocuments({
    page: pagination.pageIndex + 1,
    perPage: pagination.pageSize,
    archived: props.archived ? 1 : 0,
    ...(mappingSorting() as any),
    ...mappingFilters(debouncedColumnFilters),
    ...(documentsParam.current ? { documents: documentsParam.current } : {}),
  });

  return {
    documents: data?.data || [],
    total: data?.meta.total || 0,
    refetch,
    isLoading,
    pagination,
    setPagination,
    columnFilters,
    setColumnFilters,
    sorting,
    setSorting,
  };
};
